import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import CustomInput, {
  CustomInputLabel,
} from "src/app/components/CustomInput/CustomInput";
import { translateMessage } from "src/app/methods/translateMessage";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import { AvatarAndName } from "src/app/components/GlobalTaskDetails/tabs/GlobalActivityTab/GlobalActivityTabCampaigns";
import placeholderIcon from "src/images/placeholder-image.svg";
import { ReactComponent as RemoveIcon } from "src/images/close.svg";
import { ReactComponent as ResizeIcon } from "src/images/chevron-up-grey.svg";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import { RootState } from "src/redux/reducers";
import { TextBox, TextBoxVariable } from "src/app/components/TextBox/TextBox";
import { TextBoxWrapper } from "src/app/components/TextBoxWrapper/TextBoxWrapper";
import { IMember } from "src/app/project/ProjectTypes";
import { getFromLocalStorage } from "src/utils/localStorage";
import {
  EmailContent,
  MessageDetails,
  MessageDraft,
  MessageRecipient,
  Steps,
} from "./types";
import { CREATOR_MESSAGE_DRAFTS_KEY } from "./constants";
import { saveDraftInLocalStorage } from "./utils";

interface CreatorMessagePaneCreateEmailProps {
  messageDetails: MessageDetails;
  setStep: Dispatch<SetStateAction<Steps>>;
  emailContent: EmailContent;
  setEmailContent: Dispatch<SetStateAction<EmailContent>>;
  recipientsWithNoEmailCRM: MessageRecipient[] | undefined;
  variablesList: TextBoxVariable[];
  projectId: string;
}

export default function CreatorMessagePaneCreateEmail({
  messageDetails,
  setStep,
  emailContent,
  setEmailContent,
  recipientsWithNoEmailCRM,
  variablesList,
  projectId,
}: CreatorMessagePaneCreateEmailProps) {
  const intl = useIntl();
  const [isTextareaExpanded, setIsTextareaExpanded] = useState(false);
  const [messageText, setMessageText] = useState("");

  const { identity, activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const {
    replyToEmail,
    includeInCcCampaignMembers,
    includeInBccCampaignMembers,
    ccRecipients,
    bccRecipients,
    subject,
    message,
    recipients,
  } = emailContent;

  const recipientsWithEmails = recipients.filter(
    (recipient) => recipient.emailFromCrm,
  );

  const initialCCBCCRecipients = messageDetails.wsProjectMembers;

  const handleSetEmailContent = (
    fieldName: keyof EmailContent,
    value: string | boolean | IMember[],
  ) => {
    setEmailContent((prevState) => {
      return {
        ...prevState,
        [fieldName]: value,
      };
    });
  };

  const handleContinue = () => {
    saveDraftInLocalStorage(
      {
        message,
        subject,
      },
      activeWorkspaceUuid,
      projectId,
    );
    setStep(Steps.CONFIRM_EMAIL);
  };

  const handleRemoveRecipient = (wsTaskUuid: string) => {
    setEmailContent((prev) => ({
      ...prev,
      recipients: prev.recipients.filter(
        (recipient) => recipient.wsTaskUuid !== wsTaskUuid,
      ),
    }));
  };

  const handleRemoveCCBCCRecipient = (
    memberUuid: string,
    fieldName: "ccRecipients" | "bccRecipients",
  ) => {
    setEmailContent((prev) => {
      const newRecipients = prev[fieldName].filter(
        (member) => member.id !== memberUuid,
      );

      if (newRecipients.length === 0) {
        if (fieldName === "ccRecipients") {
          return {
            ...prev,
            includeInCcCampaignMembers: false,
            ccRecipients: [],
          };
        }
        return {
          ...prev,
          includeInBccCampaignMembers: false,
          bccRecipients: [],
        };
      }

      return {
        ...prev,
        [fieldName]: newRecipients,
      };
    });
  };

  const handleResizeEmailTextarea = () => {
    setIsTextareaExpanded((prev) => !prev);
  };

  const handleBlur = () => {
    handleSetEmailContent("message", messageText);
  };

  useEffect(() => {
    if (identity.email === undefined) return;
    handleSetEmailContent("replyToEmail", identity.email);
  }, [identity?.email]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraftInLocalStorage(
        {
          message,
          subject,
        },
        activeWorkspaceUuid,
        projectId,
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [message, subject, activeWorkspaceUuid, projectId]);

  useEffect(() => {
    const savedDrafts = getFromLocalStorage<MessageDraft>(
      CREATOR_MESSAGE_DRAFTS_KEY,
    );
    if (!savedDrafts) return;

    const draftForCurrentWorkspaceAndProject =
      savedDrafts?.[activeWorkspaceUuid]?.[projectId];

    if (draftForCurrentWorkspaceAndProject) {
      const isSubjectValid = Boolean(
        typeof draftForCurrentWorkspaceAndProject?.subject === "string",
      );
      const isMessageValid = Boolean(
        typeof draftForCurrentWorkspaceAndProject?.message === "string",
      );

      if (isSubjectValid && isMessageValid) {
        setEmailContent((prev) => ({
          ...prev,
          subject: draftForCurrentWorkspaceAndProject.subject,
          message: draftForCurrentWorkspaceAndProject.message,
        }));
      }
    }
  }, [activeWorkspaceUuid, projectId]);

  return (
    <div className="creator-message-pane__content">
      {isTextareaExpanded && (
        <button
          type="button"
          aria-label="Resize"
          className="button-style-reset creator-message-pane__email-body-resize-btn"
          onClick={handleResizeEmailTextarea}
          style={{ top: "53px" }}
        >
          <ResizeIcon style={{ transform: "rotate(180deg)" }} />
        </button>
      )}
      <div
        className="creator-message-pane__email-header"
        style={{ display: isTextareaExpanded ? "none" : "flex" }}
      >
        <div className="creator-message-pane__email-row">
          <CustomInputLabel>
            <IDHFormattedMessage id="ws_from" defaultMessage="From" />:
          </CustomInputLabel>

          <div>{messageDetails.fromEmail}</div>
        </div>
        <div className="creator-message-pane__email-row">
          <CustomInputLabel>
            <IDHFormattedMessage id="ws_to" defaultMessage="To" />:
          </CustomInputLabel>

          <div className="creator-message-pane__email-box">
            {recipientsWithEmails.map((recipient) => (
              <TooltipPortal
                content={recipient.emailFromCrm}
                key={recipient.emailFromCrm}
              >
                <div className="creator-message-pane__email-gray-label">
                  <AvatarAndName
                    name={recipient.title}
                    avatar={recipient.avatar}
                    placeholder={placeholderIcon}
                    avatarHeight={20}
                    avatarWidth={20}
                  />
                  <button
                    type="button"
                    aria-label="Remove"
                    className="button-style-reset"
                    onClick={() => handleRemoveRecipient(recipient.wsTaskUuid)}
                  >
                    <RemoveIcon />
                  </button>
                </div>
              </TooltipPortal>
            ))}
          </div>
        </div>
        {includeInCcCampaignMembers && (
          <div className="creator-message-pane__email-row">
            <CustomInputLabel>CC:</CustomInputLabel>

            <div className="creator-message-pane__email-box">
              {ccRecipients.map((recipient) => (
                <TooltipPortal content={recipient.email} key={recipient.id}>
                  <div className="creator-message-pane__email-gray-label">
                    <AvatarAndName
                      name={recipient.name}
                      avatar={recipient.avatarUrl}
                      placeholder={placeholderIcon}
                      avatarHeight={20}
                      avatarWidth={20}
                    />
                    <button
                      type="button"
                      aria-label="Remove"
                      className="button-style-reset"
                      onClick={() =>
                        handleRemoveCCBCCRecipient(recipient.id, "ccRecipients")
                      }
                    >
                      <RemoveIcon />
                    </button>
                  </div>
                </TooltipPortal>
              ))}
            </div>
          </div>
        )}
        {includeInBccCampaignMembers && (
          <div className="creator-message-pane__email-row">
            <CustomInputLabel>BCC:</CustomInputLabel>

            <div className="creator-message-pane__email-box">
              {bccRecipients.map((recipient) => (
                <TooltipPortal content={recipient.email} key={recipient.id}>
                  <div className="creator-message-pane__email-gray-label">
                    <AvatarAndName
                      name={recipient.name}
                      avatar={recipient.avatarUrl}
                      placeholder={placeholderIcon}
                      avatarHeight={20}
                      avatarWidth={20}
                    />
                    <button
                      type="button"
                      aria-label="Remove"
                      className="button-style-reset"
                      onClick={() =>
                        handleRemoveCCBCCRecipient(
                          recipient.id,
                          "bccRecipients",
                        )
                      }
                    >
                      <RemoveIcon />
                    </button>
                  </div>
                </TooltipPortal>
              ))}
            </div>
          </div>
        )}
        <div className="creator-message-pane__email-row">
          <CustomInputLabel>&nbsp;</CustomInputLabel>
          <div className="creator-message-pane__email-checkbox-wrapper">
            <span>
              <IDHFormattedMessage
                id="ws_include_campaign_members_in"
                defaultMessage="Include campaign members in"
              />
              :
            </span>
            <div className="creator-message-pane__email-checkbox">
              <CustomCheckbox
                id="includeInCcCampaignMembers"
                name="includeInCcCampaignMembers"
                label="CC"
                checked={includeInCcCampaignMembers}
                onChange={(e) => {
                  const { checked } = e.target;
                  if (checked) {
                    setEmailContent((prev) => ({
                      ...prev,
                      ccRecipients: initialCCBCCRecipients,
                      includeInBccCampaignMembers: includeInBccCampaignMembers
                        ? false
                        : prev.includeInBccCampaignMembers,
                      includeInCcCampaignMembers: checked,
                    }));
                  } else {
                    setEmailContent((prev) => ({
                      ...prev,
                      ccRecipients: [],
                      includeInCcCampaignMembers: checked,
                    }));
                  }
                }}
              />
              <CustomCheckbox
                id="includeInBccCampaignMembers"
                name="includeInBccCampaignMembers"
                label="BCC"
                checked={includeInBccCampaignMembers}
                onChange={(e) => {
                  const { checked } = e.target;
                  if (checked) {
                    setEmailContent((prev) => ({
                      ...prev,
                      bccRecipients: initialCCBCCRecipients,
                      includeInCcCampaignMembers: includeInCcCampaignMembers
                        ? false
                        : prev.includeInCcCampaignMembers,
                      includeInBccCampaignMembers: checked,
                    }));
                  } else {
                    setEmailContent((prev) => ({
                      ...prev,
                      bccRecipients: [],
                      includeInBccCampaignMembers: checked,
                    }));
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="creator-message-pane__email-row">
          <CustomInputLabel htmlFor="replyToEmail">
            <IDHFormattedMessage id="ws_reply_to" defaultMessage="Reply to" />:
          </CustomInputLabel>

          <CustomInput
            id="replyToEmail"
            type="email"
            value={replyToEmail}
            onChange={(e) =>
              handleSetEmailContent("replyToEmail", e.target.value)
            }
          />
        </div>
        <div className="creator-message-pane__email-row">
          <CustomInputLabel htmlFor="subject">
            <IDHFormattedMessage id="ws_subject" defaultMessage="Subject" />:
          </CustomInputLabel>
          <TextBoxWrapper
            className="creator-message-pane__email-subject"
            value={subject || ""}
            onChange={(newValue: any) =>
              handleSetEmailContent("subject", newValue)
            }
            toolbarMode="no-basic"
            enableEmoji
            variables={variablesList}
          />
        </div>
      </div>

      <div
        className="creator-message-pane__email-body"
        style={{ borderTop: isTextareaExpanded ? "none" : "1px solid #ededef" }}
      >
        {!isTextareaExpanded && (
          <button
            type="button"
            aria-label="Resize"
            className="button-style-reset creator-message-pane__email-body-resize-btn"
            onClick={handleResizeEmailTextarea}
            style={{ top: "-12px" }}
          >
            <ResizeIcon />
          </button>
        )}
        <TextBox
          className="creator-message-pane__email-textarea ws-input"
          value={message}
          setEncodedValue={setMessageText}
          onBlur={handleBlur}
          placeholder={translateMessage({
            intl,
            id: "ws_enter_your_message",
            defaultMessage: "Enter your message",
          })}
          variables={variablesList}
          toolbarMode="enhanced"
          enableEmoji
          bottomRightButton={
            <div className="creator-message-pane__footer creator-message-pane__footer--no-border">
              {recipientsWithNoEmailCRM?.length !== 0 && (
                <Button
                  size="large"
                  variant="white-with-light-grey-border"
                  onClick={() => setStep(Steps.MANAGE_EMAILS)}
                >
                  <IDHFormattedMessage id="ws_back" defaultMessage="Back" />
                </Button>
              )}
              <Button
                variant="blue"
                size="large"
                onClick={handleContinue}
                disabled={
                  recipientsWithEmails.length === 0 ||
                  messageText.length === 0 ||
                  subject.length === 0
                }
              >
                <IDHFormattedMessage
                  id="ws_continue"
                  defaultMessage="Continue"
                />
              </Button>
            </div>
          }
        />
      </div>
    </div>
  );
}
